// @ts-nocheck
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { formatContentDuration } from "@/lib/utils";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { useState, useEffect, useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchCourseModules,
  deleteCourseModule,
  reorderCourseModules,
  fetchTrainingModule,
  uploadTrainingMedia,
  updateTrainingModule,
  createTrainingModuleAndLink,
  importModulesFromCsv,
  patchModulePublish,
  bulkPublishCourseModules,
} from "@/lib/api/training";
import { fetchFinalExamOptions } from "@/lib/api/exams";
import { Combobox } from "@/components/ui/combobox";
import { fetchCourseDetails } from "@/lib/api/courses";
import { getYouTubeId } from "@/routes/_authenticated/training";
import { LazyVideoPlayer as VideoPlayer } from "@/components/lazy-video-player";
import { YouTubePlayer } from "@/components/youtube-player";
import { API_BASE_URL } from "@/lib/api-client";
import { validateCourseInput, type CourseInput } from "@/lib/validation/course";
import { saveCourse as saveCourseFn } from "@/lib/api/courses.functions";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormSkeleton, SectionLoader } from "@/components/loading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  BookOpen,
  CheckCircle2,
  Clock,
  FileVideo,
  GraduationCap,
  GripVertical,
  Import,
  Pencil,
  PlusCircle,
  Save,
  Trash2,
  Video,
  Upload,
  Loader2,
  FileSpreadsheet,
  Flag,
  Award,
  Tag,
  Download,
  PlayCircle,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { CourseCheckpoints } from "@/components/admin/course-checkpoints";
import { ModuleVideoCheckpoints } from "@/components/admin/module-video-checkpoints";

export const Route = createFileRoute("/_authenticated/admin/courses_/$courseId")({
  validateSearch: (search: Record<string, unknown>): { module?: string } => ({
    module: typeof search.module === "string" ? search.module : undefined,
  }),
  component: () => (
    <RequirePermission
      action="manage:courses"
      fallback={
        <AccessDenied message="Course authoring is limited to Content Creators and System Admins." />
      }
    >
      <CourseEditorPage />
    </RequirePermission>
  ),
});

const NEW = "new";

function CourseEditorPage() {
  const { courseId } = useParams({ from: "/_authenticated/admin/courses_/$courseId" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = courseId === NEW;

  /* ── Course data ── */
  const { data: course, isLoading } = useQuery({
    queryKey: ["admin-course-edit", courseId],
    enabled: !isNew,
    queryFn: async () => {
      return await fetchCourseDetails(courseId);
    },
  });

  // `modulesError` is read by the render below. Without it a failed fetch fell back to the `= []`
  // default and rendered the "No modules yet" empty state — reporting a course whose module list
  // could not be loaded as a course with no modules.
  const {
    data: courseModules = [],
    isLoading: loadingMods,
    error: modulesError,
  } = useQuery({
    queryKey: ["admin-course-modules", courseId],
    enabled: !isNew,
    queryFn: async () => {
      return await fetchCourseModules(courseId);
    },
  });

  /* ── Course form ── */
  // Declared before the exam picker below: the `exams` memo reads form.final_exam_id.
  const [form, setForm] = useState({
    title: "",
    description: "",
    is_published: false,
    final_exam_id: null as string | null,
    issues_certificate: true,
    issues_certificate_in_track: false,
  });

  // Course final-exam picker. Only CourseFinal: CourseService.ValidateFinalExamTypeAsync requires an
  // exact type match, so listing Assignment exams here (as this used to) offered options whose save
  // was guaranteed to fail. The backend still enforces the category set — this only stops the UI
  // from showing choices it knows will be rejected.
  const [examSearch, setExamSearch] = useState("");
  const { data: examResult, isLoading: examsLoading, isError: examError } = useQuery({
    queryKey: ["course-final-exams", examSearch],
    queryFn: () =>
      fetchFinalExamOptions({
        kind: "CourseFinal",
        search: examSearch,
      }),
    retry: false,
  });
  // Inject the currently-linked exam so it stays visible/selected (mirrors course-checkpoints.tsx:476-483).
  const exams = useMemo(() => {
    const rows = examResult?.items ?? [];
    const opts = rows.map((e: any) => ({
      value: e.id,
      label: e.title,
      hint: `${e.time_limit_minutes} min · pass ${e.passing_score}%${!e.is_active ? " · Draft" : ""}`,
    }));
    if (form.final_exam_id && !opts.some((o: any) => o.value === form.final_exam_id)) {
      const linked = rows.find((e: any) => e.id === form.final_exam_id);
      opts.unshift({
        value: form.final_exam_id,
        label: linked?.title ?? "Current final exam",
        hint: linked ? `${linked.time_limit_minutes} min · pass ${linked.passing_score}%${!linked.is_active ? " · Draft" : ""}` : "",
      });
    }
    return opts;
  }, [examResult, form.final_exam_id]);

  useEffect(() => {
    if (course) {
      setForm({
        title: course.title ?? "",
        description: course.description ?? "",
        is_published: course.is_published ?? false,
        final_exam_id: course.final_exam_id ?? null,
        issues_certificate: course.issues_certificate ?? true,
        issues_certificate_in_track: course.issues_certificate_in_track ?? false,
      });
    }
  }, [course]);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const callSaveCourse = useServerFn(saveCourseFn);
  const saveCourse = useMutation({
    mutationFn: async () => {
      const result = validateCourseInput({
        title: form.title,
        description: form.description,
        is_published: form.is_published,
        final_exam_id: form.final_exam_id,
        issues_certificate: form.issues_certificate,
        // Sent as false whenever certificates are off, so the stored flag can never
        // disagree with the switch the operator can actually see.
        issues_certificate_in_track:
          form.issues_certificate && form.issues_certificate_in_track,
      });
      if (!result.ok) {
        setFieldErrors(result.errors);
        throw new Error("Please fix the highlighted fields.");
      }
      setFieldErrors({});
      const res = await callSaveCourse({
        data: { id: isNew ? null : (courseId as string), data: result.data as CourseInput },
      });
      return res.id as string;
    },
    onSuccess: (id) => {
      toast.success(isNew ? "Course created" : "Course updated");
      qc.invalidateQueries({ queryKey: ["admin-courses"] });
      qc.invalidateQueries({ queryKey: ["admin-course-edit", courseId] });
      if (isNew) {
        navigate({ to: "/admin/courses/$courseId", params: { courseId: id } });
      }
    },
    onError: (e: Error) => {
      // Server-side validation errors arrive as JSON in the message.
      try {
        const parsed = JSON.parse(e.message);
        if (parsed?.code === "validation_error" && parsed.fieldErrors) {
          setFieldErrors(parsed.fieldErrors);
          toast.error("Please fix the highlighted fields.");
          return;
        }
      } catch {
        /* not a JSON payload */
      }
      toast.error(e.message);
    },
  });

  /* ── Module mutations ── */
  const removeModuleLink = useMutation({
    mutationFn: async (linkId: string) => {
      await deleteCourseModule(linkId);
    },
    onSuccess: () => {
      toast.success("Module removed from course");
      qc.invalidateQueries({ queryKey: ["admin-course-modules", courseId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const modulesQueryKey = ["admin-course-modules", courseId];

  const reorderModules = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await reorderCourseModules(courseId, orderedIds);
    },
    // Optimistic: reflect the new order immediately so a dropped module stays where it was
    // dropped instead of snapping back until the refetch lands. Rolled back on failure.
    onMutate: async (orderedIds: string[]) => {
      await qc.cancelQueries({ queryKey: modulesQueryKey });
      const previous = qc.getQueryData<any[]>(modulesQueryKey);
      if (previous) {
        const byId = new Map(previous.map((m: any) => [m.id, m]));
        const next = orderedIds.map((id) => byId.get(id)).filter(Boolean);
        // Only apply when the projection is lossless — never drop a module from the list.
        if (next.length === previous.length) qc.setQueryData(modulesQueryKey, next);
      }
      return { previous };
    },
    onError: (e: Error, _ids, ctx: any) => {
      if (ctx?.previous) qc.setQueryData(modulesQueryKey, ctx.previous);
      toast.error(e.message || "Failed to reorder modules");
    },
    // Always resync: OrderIndex (and any checkpoint range anchors the backend shifted) is
    // authoritative on the server.
    onSettled: () => qc.invalidateQueries({ queryKey: modulesQueryKey }),
  });

  /**
   * Move the module at `from` to position `to`, then persist the whole ordering.
   * The backend takes the full ordered list of module IDs and derives OrderIndex from it,
   * so an arbitrary-distance drag is a single request — no repeated neighbour swaps.
   */
  const moveTo = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    if (from >= courseModules.length || to >= courseModules.length) return;
    const ids = courseModules.map((m: any) => m.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    reorderModules.mutate(ids);
  };

  /** Arrow move by one position. `idx` is global, so this crosses page boundaries. */
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= courseModules.length) return;
    moveTo(idx, target);
    followModuleTo(target);
  };

  /**
   * OrderIndex to give the next module added to this course.
   *
   * Uses max+1 rather than "last element + 1": immediately after an optimistic reorder the
   * cached array is in the new visual order while each row's sequence_order is still the old
   * value, so the last element is not the highest index. A reorder only permutes the indices,
   * so the maximum is identical either way — and OrderIndex is uniquely constrained per course,
   * meaning a duplicate would be rejected by the database.
   */
  const nextModuleOrderIndex = useMemo(
    () =>
      courseModules.length > 0
        ? Math.max(...courseModules.map((m: any) => m.sequence_order ?? 0)) + 1
        : 0,
    [courseModules],
  );

  /* ── Multi-select + bulk delete ── */
  // Tracked by module id, not row index, so a selection survives paging and reordering.
  const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const toggleModuleSelected = (id: string) =>
    setSelectedModuleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  // Drop ids that no longer exist (deleted, or a different course loaded) so the count can
  // never claim more than is really selected.
  useEffect(() => {
    setSelectedModuleIds((prev) => {
      const live = new Set(courseModules.map((m: any) => m.id));
      const next = prev.filter((id) => live.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [courseModules]);

  const bulkDeleteModules = useMutation({
    mutationFn: async (ids: string[]) => {
      // No bulk endpoint exists, so this fans out over DELETE /modules/{id}. Capped concurrency
      // keeps a 100-module selection from opening 100 sockets at once, and each failure is
      // captured instead of aborting the run — a partial delete must still be reported honestly.
      const CONCURRENCY = 5;
      const failures: { id: string; message: string }[] = [];
      let deleted = 0;

      for (let i = 0; i < ids.length; i += CONCURRENCY) {
        const batch = ids.slice(i, i + CONCURRENCY);
        const results = await Promise.allSettled(batch.map((id) => deleteCourseModule(id)));
        results.forEach((r, j) => {
          if (r.status === "fulfilled") deleted += 1;
          else
            failures.push({
              id: batch[j],
              message: r.reason instanceof Error ? r.reason.message : String(r.reason),
            });
        });
      }

      return { deleted, failures };
    },
    onSuccess: ({ deleted, failures }) => {
      qc.invalidateQueries({ queryKey: modulesQueryKey });
      setBulkDeleteOpen(false);

      if (failures.length === 0) {
        toast.success(`Deleted ${deleted} module${deleted === 1 ? "" : "s"}`);
        setSelectedModuleIds([]);
        return;
      }

      // Keep the failed ones selected so a retry doesn't need re-picking them.
      setSelectedModuleIds(failures.map((f) => f.id));
      toast.error(
        `Deleted ${deleted} of ${deleted + failures.length}. ${failures.length} failed and stayed selected: ${failures[0].message}`,
      );
    },
    onError: (e: Error) => toast.error(e.message || "Failed to delete modules"),
  });

  const togglePublish = useMutation({
    mutationFn: async ({ id, title, rawType, published }: { id: string; title: string; rawType: string; published: boolean }) => {
      await patchModulePublish(id, title, rawType, published);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: modulesQueryKey });
      toast.success(vars.published ? "Module published" : "Module unpublished");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to toggle publish"),
  });

  const bulkPublish = useMutation({
    mutationFn: async (published: boolean) => {
      if (!courseId || isNew) throw new Error("Save the course first");
      return bulkPublishCourseModules(courseId, published);
    },
    onSuccess: (changed, published) => {
      qc.invalidateQueries({ queryKey: modulesQueryKey });
      toast.success(`${published ? "Published" : "Unpublished"} ${changed} module${changed === 1 ? "" : "s"}`);
    },
    onError: (e: Error) => toast.error(e.message || "Failed to bulk publish"),
  });

  /* ── Module list pagination ── */
  // Paginates the RENDERING only; the query still fetches the course's whole ordered list.
  // Reorder derives OrderIndex from the full ID list (and the checkpoint range picker needs
  // every module), so a page of modules is never the source of truth — page indices are
  // translated to global ones before any move.
  const MODULE_PAGE_SIZES = [10, 25, 50, 100];
  const [modulePageSize, setModulePageSize] = useState(25);
  const [modulePage, setModulePage] = useState(1);

  const moduleTotalPages = Math.max(1, Math.ceil(courseModules.length / modulePageSize));
  // Clamp instead of leaving the user on a page that no longer exists (after deletes, or after
  // switching to a larger page size).
  const currentModulePage = Math.min(modulePage, moduleTotalPages);
  const modulePageStart = (currentModulePage - 1) * modulePageSize;
  const visibleModules = courseModules.slice(modulePageStart, modulePageStart + modulePageSize);

  useEffect(() => {
    if (modulePage !== currentModulePage) setModulePage(currentModulePage);
  }, [modulePage, currentModulePage]);

  /** Jump to whichever page now holds the module at `globalIdx`, so a move stays visible. */
  const followModuleTo = (globalIdx: number) =>
    setModulePage(Math.floor(globalIdx / modulePageSize) + 1);

  /* ── Drag-and-drop reordering ── */
  // `armedId` gates the row's `draggable` flag on a mousedown/touch on the grip, so text
  // selection and the row's action buttons keep working; only the handle starts a drag.
  // dragIndex/dragOverIndex hold GLOBAL indices, not page-relative ones.
  const [dragArmedId, setDragArmedId] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const resetDrag = () => {
    setDragArmedId(null);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  /* ── UI state ── */
  const [moduleFormOpen, setModuleFormOpen] = useState(false);
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const [reviewModuleId, setReviewModuleId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  // Deep-link: the curriculum page links here with ?module=… to open the review dialog.
  // An effect rather than seeded initial state because the route stays mounted when only
  // the search changes — following a second link would otherwise be ignored.
  const searchParams = Route.useSearch();
  useEffect(() => {
    if (!searchParams.module) return;
    setReviewModuleId(searchParams.module);
  }, [searchParams.module]);

  if (!isNew && isLoading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <FormSkeleton fields={6} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/admin/courses"
            className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to courses
          </Link>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2 mt-2">
            <GraduationCap className="h-8 w-8" />
            {isNew ? "Create Course" : form.title || "Edit Course"}
          </h1>
          {!isNew && course?.created_by_name && (
            <p className="text-sm text-muted-foreground mt-1">
              Created by <span className="font-medium text-foreground">{course.created_by_name}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Button variant="outline" asChild>
              <Link to="/admin/courses/$courseId/curriculum" params={{ courseId }}>
                <BookOpen className="h-4 w-4 mr-2" />
                Curriculum &amp; Assessments
              </Link>
            </Button>
          )}
          <Button onClick={() => saveCourse.mutate()} disabled={!form.title || saveCourse.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {saveCourse.isPending ? "Saving…" : isNew ? "Create Course" : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Course details */}
      <Card>
        <CardHeader>
          <CardTitle>Course Details</CardTitle>
          <CardDescription>Basic information shown to students.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Course Title *</Label>
            <Input
              id="title"
              value={form.title}
              maxLength={120}
              autoComplete="off"
              aria-invalid={!!fieldErrors.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g., Advanced Teaching Methods"
            />
            {fieldErrors.title && <p className="text-xs text-destructive">{fieldErrors.title}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              rows={3}
              value={form.description}
              maxLength={2000}
              autoComplete="off"
              aria-invalid={!!fieldErrors.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What will students learn in this course?"
            />
            <div className="flex justify-between text-xs">
              <span className="text-destructive">{fieldErrors.description ?? ""}</span>
              <span className="text-muted-foreground">{form.description.length}/2000</span>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {/* Derived, not editable: the course's length is the sum of its modules' durations,
                recomputed by the API on every module change. */}
            <div className="space-y-2">
              <Label>Duration</Label>
              <div className="flex h-9 items-center gap-2 rounded-md border border-input bg-muted/50 px-3 text-sm">
                <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="font-medium tabular-nums">
                  {formatContentDuration(course?.total_duration_seconds)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Added up from this course&apos;s modules.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Final Exam (optional)</Label>
              <Combobox
                value={form.final_exam_id ?? null}
                onChange={(v) => setForm({ ...form, final_exam_id: v })}
                onSearchChange={setExamSearch}
                isLoading={examsLoading}
                placeholder="No exam linked"
                searchPlaceholder="Search Final Exams…"
                emptyMessage={examError
                  ? "Failed to load Course Final exams. Please try again."
                  : "No Final Exams exist yet — create one in the Exam Center."}
                options={exams}
              />
              {examError && (
                <p className="text-xs text-destructive">Failed to load Course Final exams. Check permissions or refresh.</p>
              )}
              {fieldErrors.final_exam_id && (
                <p className="text-xs text-destructive">{fieldErrors.final_exam_id}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="published"
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
            />
            <Label htmlFor="published" className="cursor-pointer">
              Publish course (visible to students)
            </Label>
          </div>

          <div className="rounded-lg border border-border/40 bg-muted/40">
            <div className="flex items-center justify-between p-3">
              <div className="space-y-0.5">
                <p className="text-sm font-semibold flex items-center gap-1.5">
                  <Award className="h-4 w-4 text-amber-500" />
                  Issue Certificate on Completion
                </p>
                <p className="text-xs text-muted-foreground">
                  When disabled, finishing this course will never generate a certificate.
                </p>
              </div>
              <Switch
                aria-label="Issue Certificate on Completion"
                checked={form.issues_certificate}
                onCheckedChange={(checked) => setForm({ ...form, issues_certificate: checked })}
              />
            </div>

            {/* Nested, and disabled when the parent is off, because it has no effect on its own:
                a student who never earns a course certificate cannot earn one inside a track. */}
            <div className="flex items-center justify-between border-t border-border/40 p-3 pl-6">
              <div className="space-y-0.5">
                <p
                  className={`text-sm font-medium ${form.issues_certificate ? "" : "text-muted-foreground"}`}
                >
                  Also issue it inside a track
                </p>
                <p className="text-xs text-muted-foreground">
                  By default a course taken as part of a track counts only toward the track's own
                  certificate. Turn this on to award this course's certificate as well.
                </p>
              </div>
              <Switch
                aria-label="Also issue it inside a track"
                checked={form.issues_certificate && form.issues_certificate_in_track}
                disabled={!form.issues_certificate}
                onCheckedChange={(checked) =>
                  setForm({ ...form, issues_certificate_in_track: checked })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modules section — only after course exists */}
      {isNew ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Save the course first to start adding training modules.
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="modules" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="modules" className="text-sm">
              <BookOpen className="h-4 w-4 mr-2" /> Modules
            </TabsTrigger>
            <TabsTrigger value="checkpoints" className="text-sm">
              <Flag className="h-4 w-4 mr-2" /> Checkpoints
            </TabsTrigger>
          </TabsList>

          <TabsContent value="modules" className="mt-0 space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Training Modules</CardTitle>
                  <CardDescription>
                    Modules students will complete, in order. {courseModules.length} module
                    {courseModules.length === 1 ? "" : "s"}.
                    {courseModules.length > 1 &&
                      " Drag the handle to reorder within a page; the arrows move a module one" +
                        " position and follow it across pages."}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCsvImportOpen(true)}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" /> Import from CSV
                  </Button>
                  {courseModules.some((cm: any) => cm.module && !cm.module.is_published) && (
                    <Button
                      variant="outline"
                      disabled={bulkPublish.isPending}
                      onClick={() => bulkPublish.mutate(true)}
                    >
                      {bulkPublish.isPending ? "Publishing…" : "Publish All Drafts"}
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setEditingModuleId(null);
                      setModuleFormOpen(true);
                    }}
                  >
                    <PlusCircle className="h-4 w-4 mr-2" /> Add module
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {moduleFormOpen && (
                  <ModuleForm
                    courseId={courseId}
                    nextSequence={nextModuleOrderIndex}
                    editingModuleId={editingModuleId}
                    onClose={() => {
                      setModuleFormOpen(false);
                      setEditingModuleId(null);
                    }}
                  />
                )}

                {courseModules.length > 0 && (
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                    <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                      <Checkbox
                        checked={
                          visibleModules.length > 0 &&
                          visibleModules.every((m: any) => selectedModuleIds.includes(m.id))
                        }
                        onCheckedChange={(checked) => {
                          const pageIds = visibleModules.map((m: any) => m.id);
                          setSelectedModuleIds((prev) =>
                            checked
                              ? [...prev, ...pageIds.filter((id: string) => !prev.includes(id))]
                              : prev.filter((id) => !pageIds.includes(id)),
                          );
                        }}
                        aria-label="Select all modules on this page"
                      />
                      Select page
                    </label>

                    {selectedModuleIds.length < courseModules.length && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() =>
                          setSelectedModuleIds(courseModules.map((m: any) => m.id))
                        }
                      >
                        Select all {courseModules.length}
                      </Button>
                    )}

                    {selectedModuleIds.length > 0 && (
                      <>
                        <span className="text-xs text-muted-foreground">
                          {selectedModuleIds.length} selected
                        </span>
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs"
                          onClick={() => setSelectedModuleIds([])}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-8 ml-auto"
                          disabled={bulkDeleteModules.isPending}
                          onClick={() => setBulkDeleteOpen(true)}
                        >
                          {bulkDeleteModules.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5 mr-2" />
                          )}
                          Delete selected
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {loadingMods ? (
                  <p className="text-sm text-muted-foreground">Loading modules…</p>
                ) : modulesError ? (
                  <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-8 text-center text-sm">
                    <p className="font-semibold text-destructive">Couldn&apos;t load this course&apos;s modules.</p>
                    <p className="mt-1 text-muted-foreground">
                      {modulesError instanceof Error ? modulesError.message : String(modulesError)}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => qc.invalidateQueries({ queryKey: modulesQueryKey })}
                    >
                      Retry
                    </Button>
                  </div>
                ) : courseModules.length === 0 ? (
                  <div className="border border-dashed rounded-lg p-8 text-center text-sm text-muted-foreground">
                    No modules yet. Add one above, or import from another course.
                  </div>
                ) : (
                  visibleModules.map((cm: any, localIdx: number) => {
                    // Global position in the course. Every downstream use — the displayed
                    // number, the arrow bounds, the drag indices, the reorder payload — is in
                    // global terms, so page 3's first row is module 51, not module 1.
                    const idx = modulePageStart + localIdx;
                    const isDragging = dragIndex === idx;
                    const isSelected = selectedModuleIds.includes(cm.id);
                    // Where the module would land: above the hovered row when dragging upward,
                    // below it when dragging downward.
                    const showIndicator =
                      dragIndex !== null && dragOverIndex === idx && dragIndex !== idx;
                    const indicatorAbove = showIndicator && dragIndex! > idx;
                    const indicatorBelow = showIndicator && dragIndex! < idx;

                    return (
                      <div key={cm.id} className="relative">
                        {indicatorAbove && (
                          <div className="absolute -top-2 left-0 right-0 h-0.5 rounded-full bg-primary" />
                        )}
                        {indicatorBelow && (
                          <div className="absolute -bottom-2 left-0 right-0 h-0.5 rounded-full bg-primary" />
                        )}
                        <div
                          draggable={dragArmedId === cm.id}
                          onDragStart={(e) => {
                            setDragIndex(idx);
                            e.dataTransfer.effectAllowed = "move";
                            // Firefox refuses to start a drag without payload.
                            e.dataTransfer.setData("text/plain", cm.id);
                          }}
                          onDragOver={(e) => {
                            if (dragIndex === null) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            if (dragOverIndex !== idx) setDragOverIndex(idx);
                          }}
                          onDragLeave={() =>
                            setDragOverIndex((cur) => (cur === idx ? null : cur))
                          }
                          onDrop={(e) => {
                            e.preventDefault();
                            if (dragIndex !== null) moveTo(dragIndex, idx);
                            resetDrag();
                          }}
                          onDragEnd={resetDrag}
                          className={`flex items-center gap-3 p-3 rounded-lg border bg-card group transition-opacity ${
                            isDragging ? "opacity-40 border-primary" : ""
                          } ${isSelected ? "border-primary/60 bg-primary/5" : ""}`}
                        >
                          <Checkbox
                            className="shrink-0"
                            checked={isSelected}
                            onCheckedChange={() => toggleModuleSelected(cm.id)}
                            aria-label={`Select ${cm.module?.title ?? "module"}`}
                          />
                          <div
                            role="button"
                            tabIndex={-1}
                            aria-hidden="true"
                            title="Drag to reorder"
                            className="shrink-0 -ml-1 p-1 rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted cursor-grab active:cursor-grabbing touch-none"
                            onMouseDown={() => setDragArmedId(cm.id)}
                            onTouchStart={() => setDragArmedId(cm.id)}
                            onMouseUp={() => setDragArmedId(null)}
                          >
                            <GripVertical className="h-4 w-4" />
                          </div>
                          <div className="flex flex-col">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              aria-label="Move module up"
                              title="Move up"
                              disabled={idx === 0}
                              onClick={() => move(idx, -1)}
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              aria-label="Move module down"
                              title="Move down"
                              disabled={idx === courseModules.length - 1}
                              onClick={() => move(idx, 1)}
                            >
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="h-9 w-9 rounded bg-primary/10 flex items-center justify-center shrink-0">
                            {cm.module?.content_type === "youtube" ? (
                              <Video className="h-4 w-4 text-primary" />
                            ) : (
                              <FileVideo className="h-4 w-4 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-sm truncate">
                                {idx + 1}. {cm.module?.title ?? "Untitled"}
                              </p>
                              {cm.module && !cm.module.is_published && (
                                <Badge
                                  variant="outline"
                                  className="cursor-pointer hover:bg-primary/10"
                                  title="Click to publish this module"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePublish.mutate({
                                      id: cm.module_id,
                                      title: cm.module.title ?? "Untitled",
                                      rawType: cm.module.raw_type ?? "Video",
                                      published: true,
                                    });
                                  }}
                                >
                                  Draft — click to publish
                                </Badge>
                              )}
                              {cm.module && cm.module.is_published && (
                                <Badge
                                  variant="secondary"
                                  className="cursor-pointer hover:bg-destructive/10 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Click to unpublish this module"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePublish.mutate({
                                      id: cm.module_id,
                                      title: cm.module.title ?? "Untitled",
                                      rawType: cm.module.raw_type ?? "Video",
                                      published: false,
                                    });
                                  }}
                                >
                                  Unpublish
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              {cm.module?.description || "No description"}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title="Review video"
                            onClick={() => setReviewModuleId(cm.module_id)}
                          >
                            <PlayCircle className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title="Edit module"
                            onClick={() => {
                              setEditingModuleId(cm.module_id);
                              setModuleFormOpen(true);
                            }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            title="Remove module"
                            onClick={() => {
                              if (confirm("Remove this module from the course?")) {
                                removeModuleLink.mutate(cm.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}

                {courseModules.length > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        Showing {modulePageStart + 1}–
                        {modulePageStart + visibleModules.length} of {courseModules.length}
                      </span>
                      <span className="text-muted-foreground/50">|</span>
                      <label htmlFor="module-page-size" className="whitespace-nowrap">
                        Per page
                      </label>
                      <select
                        id="module-page-size"
                        className="h-8 rounded-md border bg-background px-2 text-xs"
                        value={modulePageSize}
                        onChange={(e) => {
                          const nextSize = Number(e.target.value);
                          // Keep the first currently-visible module on screen after the change.
                          setModulePage(Math.floor(modulePageStart / nextSize) + 1);
                          setModulePageSize(nextSize);
                        }}
                      >
                        {MODULE_PAGE_SIZES.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={currentModulePage === 1}
                        onClick={() => setModulePage(1)}
                        title="First page"
                      >
                        <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
                        <span className="sr-only">First page</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={currentModulePage === 1}
                        onClick={() => setModulePage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <span className="px-2 text-xs font-medium whitespace-nowrap">
                        Page {currentModulePage} of {moduleTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={currentModulePage === moduleTotalPages}
                        onClick={() => setModulePage((p) => Math.min(moduleTotalPages, p + 1))}
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={currentModulePage === moduleTotalPages}
                        onClick={() => setModulePage(moduleTotalPages)}
                        title="Last page"
                      >
                        <ChevronsRight className="h-4 w-4" aria-hidden="true" />
                        <span className="sr-only">Last page</span>
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="checkpoints" className="mt-0 space-y-4">
            <CourseCheckpoints courseId={courseId} courseModules={courseModules} />
          </TabsContent>
        </Tabs>
      )}

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedModuleIds.length} module
              {selectedModuleIds.length === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They will be removed from this course for students. Any checkpoint whose range was
              anchored to a deleted module keeps its position, so review the Checkpoints tab
              afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleteModules.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={bulkDeleteModules.isPending}
              onClick={(e) => {
                // Keep the dialog mounted while the run is in flight; it closes in onSuccess.
                e.preventDefault();
                bulkDeleteModules.mutate(selectedModuleIds);
              }}
            >
              {bulkDeleteModules.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Deleting…
                </>
              ) : (
                `Delete ${selectedModuleIds.length}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ModuleReviewDialog moduleId={reviewModuleId} onClose={() => setReviewModuleId(null)} />

      {!isNew && (
        <ImportFromCourseDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          courseId={courseId}
          existingModuleIds={courseModules.map((c: any) => c.module_id)}
          nextSequence={nextModuleOrderIndex}
        />
      )}

      {!isNew && (
        <ImportFromCSVDialog
          open={csvImportOpen}
          onClose={() => setCsvImportOpen(false)}
          courseId={courseId}
          nextSequence={nextModuleOrderIndex}
        />
      )}
    </div>
  );
}

/* ── Module Review (admin preview, no seek/progress restrictions) ── */
function ModuleReviewDialog({
  moduleId,
  onClose,
}: {
  moduleId: string | null;
  onClose: () => void;
}) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!moduleId) {
      setData(null);
      return;
    }
    setLoading(true);
    fetchTrainingModule(moduleId)
      .then(setData)
      .catch(() => toast.error("Failed to load module for review"))
      .finally(() => setLoading(false));
  }, [moduleId]);

  // fetchTrainingModule already normalizes the backend's ModuleDetailDto (type/contentUrl)
  // into the snake_case shape the admin forms read, so read `content_type`/`video_url` here —
  // NOT the raw `type`/`contentUrl`, which the normalizer does not pass through. Both
  // YoutubeLink and Video content live in contentUrl backend-side, hence in video_url here.
  const contentType = data?.content_type ?? "";
  const contentUrl = data?.video_url || null;
  const ytId = contentType === "youtube" && contentUrl ? getYouTubeId(contentUrl) : null;
  const uploadedVideoSrc =
    contentType === "uploaded_video" && contentUrl
      ? contentUrl.startsWith("http")
        ? contentUrl
        : `${(API_BASE_URL ?? "").replace(/\/api\/?$/, "")}${contentUrl}`
      : null;

  return (
    <Dialog open={!!moduleId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Review: {data?.title || "Video"}</DialogTitle>
          <DialogDescription>
            Preview exactly what students see. Seeking is unrestricted here so you can scrub through
            the whole video while testing.
          </DialogDescription>
        </DialogHeader>
        <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex h-full items-center justify-center text-white/60 text-sm">
              Loading…
            </div>
          ) : ytId ? (
            <YouTubePlayer
              key={ytId}
              videoId={ytId}
              isCompleted
              allowSeek
              className="w-full h-full"
            />
          ) : uploadedVideoSrc ? (
            <VideoPlayer src={uploadedVideoSrc} isCompleted allowSeek className="w-full h-full" />
          ) : (
            <div className="flex h-full items-center justify-center text-white/60 text-sm">
              No video attached to this module.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Module Form ───────────────────────── */
function ModuleForm({
  courseId,
  nextSequence,
  editingModuleId,
  onClose,
}: {
  courseId: string;
  nextSequence: number;
  editingModuleId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  // Read-only server-side path - used only to show the check indicator for an
  // existing resource file. Never sent back to the backend.
  const [existingResourceUrl, setExistingResourceUrl] = useState<string | null>(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    video_url: "",
    content_type: "youtube",
    duration_seconds: 0,
    videoFile: null as File | null,
    documentFile: null as File | null,
    // ExternalResource only
    external_url: "",
    provider_name: "",
    open_in_new_tab: true,
    estimated_minutes: "" as string | number,
  });
  useEffect(() => {
    if (!editingModuleId) return;
    (async () => {
      const data = await fetchTrainingModule(editingModuleId);
      if (data) {
        setForm({
          title: data.title ?? "",
          description: data.description ?? "",
          video_url: data.video_url ?? "",
          content_type: data.content_type ?? "youtube",
          duration_seconds: data.duration_seconds ?? 0,
          videoFile: null,
          documentFile: null,
          external_url: data.external_url ?? "",
          provider_name: data.provider_name ?? "",
          open_in_new_tab: data.open_in_new_tab ?? true,
          estimated_minutes: data.estimated_minutes ?? "",
        });
        setExistingResourceUrl(data.existing_resource_url ?? null);
      }
    })();
  }, [editingModuleId]);

  const handleFileUpload = (file: File, type: "video" | "document") => {
    if (type === "video") {
      setForm((f) => ({ ...f, videoFile: file, content_type: "uploaded_video" }));
    } else {
      setForm((f) => ({ ...f, documentFile: file }));
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      // Validate YouTube URL format when content type is youtube.
      if (form.content_type === "youtube") {
        if (!form.video_url || !form.video_url.startsWith("https://")) {
          throw new Error("YouTube link must start with https://");
        }
      }
      // Mirrors ModuleService.ValidateExternalResource so the author gets the error inline
      // instead of a round-trip 400. The server check remains the authority.
      if (form.content_type === "external_resource") {
        const url = form.external_url?.trim();
        if (!url) throw new Error("Resource URL is required.");
        let parsed: URL;
        try {
          parsed = new URL(url);
        } catch {
          throw new Error("Resource URL must be a valid absolute URL (http:// or https://).");
        }
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          throw new Error("Resource URL must begin with http:// or https://.");
        }
        if (!form.provider_name?.trim()) throw new Error("Provider is required.");
        if (form.estimated_minutes !== "" && Number(form.estimated_minutes) <= 0) {
          throw new Error("Estimated duration must be greater than 0.");
        }
      }
      if (editingModuleId) {
        await updateTrainingModule(editingModuleId, form);
      } else {
        await createTrainingModuleAndLink(form, courseId, nextSequence);
      }
    },
    onSuccess: () => {
      toast.success("Lesson saved");
      qc.invalidateQueries({ queryKey: ["admin-course-modules", courseId] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="mb-2 p-5 rounded-xl border-2 border-primary/30 bg-primary/5 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-lg">{editingModuleId ? "Edit Lesson" : "New Lesson"}</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Lesson Title *</Label>
          <Input
            value={form.title}
            autoComplete="off"
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Intro to the Platform"
          />
        </div>
        <div className="space-y-2">
          <Label>Module Type</Label>
          <Select
            value={form.content_type}
            onValueChange={(v: any) => setForm({ ...form, content_type: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="youtube">YouTube Video</SelectItem>
              <SelectItem value="uploaded_video">Self-Hosted Video</SelectItem>
              <SelectItem value="document">Document / Resource</SelectItem>
              <SelectItem value="external_resource">External Resource (link)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 space-y-2">
          <Label>Description</Label>
          <Textarea
            rows={2}
            value={form.description}
            autoComplete="off"
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        {form.content_type === "external_resource" && (
          <>
            <div className="space-y-2">
              <Label>Resource URL *</Label>
              <Input
                value={form.external_url}
                autoComplete="off"
                onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                placeholder="https://www.coursera.org/learn/..."
              />
            </div>
            <div className="space-y-2">
              <Label>Provider *</Label>
              <Input
                value={form.provider_name}
                autoComplete="off"
                onChange={(e) => setForm({ ...form, provider_name: e.target.value })}
                placeholder="e.g. Coursera, MaharaTech, Udemy"
              />
            </div>
            <div className="space-y-2">
              <Label>Estimated Duration (minutes)</Label>
              <Input
                type="number"
                min={1}
                value={form.estimated_minutes}
                autoComplete="off"
                onChange={(e) => setForm({ ...form, estimated_minutes: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-2">
              <Label>Link Behaviour</Label>
              <label className="flex items-center gap-2 text-sm h-9">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary cursor-pointer"
                  checked={form.open_in_new_tab}
                  onChange={(e) => setForm({ ...form, open_in_new_tab: e.target.checked })}
                />
                Open in a new tab
              </label>
            </div>
          </>
        )}
        {form.content_type === "youtube" && (
          <div className="space-y-2">
            <Label>YouTube Link</Label>
            <Input
              value={form.video_url}
              autoComplete="off"
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </div>
        )}
        {form.content_type === "uploaded_video" && (
          <div className="space-y-2">
            <Label>Video File</Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept="video/*"
                onChange={(e) =>
                  e.target.files?.[0] && handleFileUpload(e.target.files[0], "video")
                }
              />
              {form.videoFile && (
                <CheckCircle2 className="text-green-500 h-4 w-4 shrink-0" />
              )}
            </div>
          </div>
        )}
        {form.content_type === "document" && (
          <div className="space-y-2">
            <Label>Reading Materials (PDF / Doc)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) =>
                  e.target.files?.[0] && handleFileUpload(e.target.files[0], "document")
                }
              />
              {(form.documentFile || existingResourceUrl) && (
                <CheckCircle2 className="text-green-500 h-4 w-4 shrink-0" />
              )}
            </div>
          </div>
        )}
        <div className="space-y-2">
          <Label>Duration (min)</Label>
          <Input
            type="number"
            min={0}
            value={Math.ceil(form.duration_seconds / 60)}
            onChange={(e) => setForm({ ...form, duration_seconds: Number(e.target.value) * 60 })}
          />
          {form.content_type === "youtube" && (
            // The server looks the real length up from YouTube on save and stores that instead, so
            // this input only matters as a fallback (private video, or no API key configured).
            <p className="text-xs text-muted-foreground">
              Read from YouTube on save — only used if the video&apos;s length can&apos;t be fetched.
            </p>
          )}
        </div>
      </div>

      {/* In-video checkpoints: only for an already-saved video module (they key off its id and
          duration). Hidden for documents and for a not-yet-created module. */}
      {editingModuleId &&
        (form.content_type === "youtube" || form.content_type === "uploaded_video") && (
          <ModuleVideoCheckpoints
            moduleId={editingModuleId}
            durationSeconds={form.duration_seconds}
          />
        )}

      <div className="flex justify-end pt-2">
        <Button
          onClick={() => save.mutate()}
          disabled={!form.title || save.isPending}
          className="min-w-[140px]"
        >
          <Save className="h-4 w-4 mr-2" />
          {save.isPending ? "Saving…" : editingModuleId ? "Update Lesson" : "Save Lesson"}
        </Button>
      </div>
    </div>
  );
}

function ImportFromCourseDialog({
  open,
  onClose,
  courseId,
  existingModuleIds,
  nextSequence,
}: {
  open: boolean;
  onClose: () => void;
  courseId: string;
  existingModuleIds: string[];
  nextSequence: number;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (open) setSelected([]);
  }, [open]);

  const { data: otherCourses = [], isLoading } = useQuery({
    queryKey: ["import-source-courses", courseId],
    enabled: open,
    queryFn: async () => {
      return await fetchCourseModulesForImport();
    },
  });

  const importMods = useMutation({
    mutationFn: async () => {
      if (!selected.length) return;

      await importModulesFromCourse(courseId, selected, nextSequence);
    },
    onSuccess: () => {
      toast.success(
        `Imported and copied ${selected.length} module${selected.length === 1 ? "" : "s"}`,
      );
      qc.invalidateQueries({ queryKey: ["admin-course-modules", courseId] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = (id: string) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import modules from another course</DialogTitle>
          <DialogDescription>
            Pick modules from existing courses to copy into this one. Copies are independent —
            editing them here will not affect the original courses.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isLoading ? (
            <SectionLoader label="Loading courses" className="py-6" />
          ) : otherCourses.length === 0 ? (
            <p className="text-sm text-muted-foreground">No other courses available.</p>
          ) : (
            otherCourses.map((c: any) => {
              const mods = (c.modules ?? []).map((m: any) => m.module).filter(Boolean);
              if (!mods.length) return null;
              return (
                <div key={c.id} className="border rounded-lg p-4 space-y-2">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-primary" />
                    {c.title}
                    <Badge variant="outline" className="ml-auto">
                      {mods.length} module{mods.length === 1 ? "" : "s"}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    {mods.map((m: any) => {
                      const already = existingModuleIds.includes(m.id);
                      return (
                        <label
                          key={m.id}
                          className={`flex items-start gap-3 p-2 rounded hover:bg-accent/40 ${already ? "opacity-50" : "cursor-pointer"
                            }`}
                        >
                          <input
                            type="checkbox"
                            disabled={already}
                            checked={selected.includes(m.id)}
                            onChange={() => toggle(m.id)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {m.title}
                              {already && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (already in course)
                                </span>
                              )}
                            </div>
                            {m.description && (
                              <div className="text-xs text-muted-foreground line-clamp-1">
                                {m.description}
                              </div>
                            )}
                          </div>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {m.content_type}
                          </Badge>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => importMods.mutate()}
            disabled={selected.length === 0 || importMods.isPending}
          >
            {importMods.isPending
              ? "Importing…"
              : `Import ${selected.length} module${selected.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────── Helper Functions for CSV Import ─────────────────── */

function detectDelimiter(text: string): string {
  const delimiters = [",", ";", "\t"];
  const lines = text.split("\n").slice(0, 5); // check first 5 lines
  const counts = { ",": 0, ";": 0, "\t": 0 };

  for (const line of lines) {
    for (const d of delimiters) {
      const escapedD = d === "\t" ? "\\t" : d;
      const matches = line.match(new RegExp(escapedD, "g"));
      if (matches) {
        counts[d] += matches.length;
      }
    }
  }

  let bestDelimiter = ",";
  let maxCount = 0;
  for (const d of delimiters) {
    if (counts[d] > maxCount) {
      maxCount = counts[d];
      bestDelimiter = d;
    }
  }
  return bestDelimiter;
}

function parseCSV(text: string, delimiter: string = ","): string[][] {
  const lines: string[][] = [];
  let row: string[] = [""];
  let insideQuote = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (insideQuote && nextChar === '"') {
        row[row.length - 1] += '"';
        i++;
      } else {
        insideQuote = !insideQuote;
      }
    } else if (char === delimiter && !insideQuote) {
      row.push("");
    } else if ((char === "\r" || char === "\n") && !insideQuote) {
      if (char === "\r" && nextChar === "\n") {
        i++;
      }
      lines.push(row);
      row = [""];
    } else {
      row[row.length - 1] += char;
    }
  }
  if (row.length > 1 || row[0] !== "") {
    lines.push(row);
  }
  return lines;
}

function parseDurationToSeconds(durationStr: string): number {
  if (!durationStr) return 0;
  const cleanStr = durationStr.trim();

  // Handle HH:MM:SS or MM:SS format
  if (cleanStr.includes(":")) {
    const parts = cleanStr.split(":").map(Number);
    if (parts.some(isNaN)) return 0;
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
  }

  // Handle standard integer seconds
  const num = parseInt(cleanStr, 10);
  if (!isNaN(num)) return num;

  return 0;
}

function formatSecondsToReadable(sec: number): string {
  if (sec <= 0) return "0s";
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);
  return parts.join(" ");
}

/* ─────────────────── Import-from-CSV Dialog ─────────────────── */
interface ParsedModule {
  title: string;
  description: string;
  video_url: string;
  duration_seconds: number;
  /** Set when the server would reject this row outright. Such rows start deselected. */
  issue?: string;
  /** Informational only — the row still imports and stays selected. */
  note?: string;
}

function ImportFromCSVDialog({
  open,
  onClose,
  courseId,
  nextSequence,
}: {
  open: boolean;
  onClose: () => void;
  courseId: string;
  nextSequence: number;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [parsedModules, setParsedModules] = useState<ParsedModule[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  // Rows the parser could not turn into a module, and rows the server rejected — both are
  // shown to the user instead of quietly shrinking the import.
  const [skippedRows, setSkippedRows] = useState<{ row: number; reason: string }[]>([]);
  const [serverErrors, setServerErrors] = useState<
    { row: number; title: string; errorMessage: string }[]
  >([]);
  const [columnMapping, setColumnMapping] = useState<{
    titleIdx: number;
    descriptionIdx: number;
    urlIdx: number;
    durationIdx: number;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setErrorMsg(null);
      setParsedModules([]);
      setColumnMapping(null);
      setSelectedIndices([]);
      setSkippedRows([]);
      setServerErrors([]);
    }
  }, [open]);

  // Default selection excludes rows the server would reject, so the count in the button is
  // the count that will actually import.
  useEffect(() => {
    setSelectedIndices(parsedModules.map((_, idx) => idx).filter((idx) => !parsedModules[idx].issue));
  }, [parsedModules]);

  const handleDownloadTemplate = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      "Title/Focus,Description,Link/URL,Duration\n" +
      '"Introduction to Course","What the course covers and who it is for",https://youtube.com/watch?v=example1,10:00\n' +
      '"Advanced Techniques","Deeper patterns, with worked examples",https://youtube.com/watch?v=example2,25:30\n' +
      // Every row carries a link and a duration: the import endpoint rejects rows missing
      // either, so the template must not model a row that cannot be imported.
      '"Final Review","Recap and next steps",https://youtube.com/watch?v=example3,05:00';
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "course_modules_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".csv")) {
      setErrorMsg("Please upload a valid .csv file.");
      setFile(null);
      setParsedModules([]);
      setColumnMapping(null);
      return;
    }

    setFile(selectedFile);
    setErrorMsg(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;

        // Detect Excel/Office files mislabeled as .csv. XLSX/DOCX are ZIP
        // archives (start with "PK"); legacy .xls starts with the OLE magic
        // bytes. Either way the content isn't parseable text.
        if (text.startsWith("PK") || text.startsWith("\xD0\xCF\x11\xE0")) {
          setErrorMsg(
            "This looks like an Excel file, not a CSV. In Excel choose File → Save As → CSV (Comma delimited), then upload that file.",
          );
          setFile(null);
          setParsedModules([]);
          setColumnMapping(null);
          return;
        }

        const delimiter = detectDelimiter(text);
        const csvRows = parseCSV(text, delimiter);
        if (csvRows.length < 1) {
          setErrorMsg("The CSV file is empty.");
          return;
        }

        // Find the header row by searching for columns containing keywords
        let headerRowIdx = 0;
        for (let r = 0; r < Math.min(csvRows.length, 30); r++) {
          const row = csvRows[r];
          const hasTitleKeyword = row.some((cell) => {
            const c = cell.trim().toLowerCase();
            return (
              c.includes("skill") ||
              c.includes("focus") ||
              c.includes("option") ||
              c.includes("module name") ||
              c.includes("lesson")
            );
          });
          const hasLinkKeyword = row.some((cell) => {
            const c = cell.trim().toLowerCase();
            return (
              c.includes("link") ||
              c.includes("url") ||
              c.includes("youtube") ||
              c.includes("video")
            );
          });
          if (hasTitleKeyword || (hasLinkKeyword && row.length > 2)) {
            headerRowIdx = r;
            break;
          }
        }

        const headers = csvRows[headerRowIdx].map((h) => h.trim().toLowerCase());

        // Match columns intelligently
        let titleIdx = headers.findIndex(
          (h) =>
            h.includes("skill") ||
            h.includes("focus") ||
            h.includes("title") ||
            h.includes("option") ||
            h.includes("lesson"),
        );
        if (titleIdx === -1) {
          titleIdx = headers.findIndex(
            (h) => h.includes("name") && !h.includes("track") && !h.includes("module"),
          );
        }
        let urlIdx = headers.findIndex(
          (h) =>
            h.includes("link") || h.includes("url") || h.includes("youtube") || h.includes("video"),
        );
        let durationIdx = headers.findIndex(
          (h) => h.includes("duration") || h.includes("time") || h.includes("length"),
        );

        // Fallbacks if headers are not explicitly matched
        if (titleIdx === -1) titleIdx = 1; // commonly the 2nd column
        if (urlIdx === -1) urlIdx = 3; // commonly the 4th column
        if (durationIdx === -1) durationIdx = 4; // commonly the 5th column

        // Description is optional and has no positional fallback — guessing a column here
        // would import an unrelated cell (a note, an owner name) as the lesson description.
        // Dropped when it collides with a column already claimed by another field.
        let descriptionIdx = headers.findIndex(
          (h) => h.includes("desc") || h.includes("summary") || h.includes("details"),
        );
        if (
          descriptionIdx === titleIdx ||
          descriptionIdx === urlIdx ||
          descriptionIdx === durationIdx
        ) {
          descriptionIdx = -1;
        }

        setColumnMapping({ titleIdx, descriptionIdx, urlIdx, durationIdx });

        const modules: ParsedModule[] = [];
        const skipped: { row: number; reason: string }[] = [];
        const cell = (row: string[], idx: number) => (idx >= 0 ? (row[idx]?.trim() ?? "") : "");

        for (let i = headerRowIdx + 1; i < csvRows.length; i++) {
          const row = csvRows[i];
          // Genuinely empty rows (blank line, or a row of empty cells) aren't reported — they
          // are spreadsheet padding, not lost data.
          if (row.length === 0 || row.every((c) => c.trim() === "")) continue;

          const title = cell(row, titleIdx);
          const url = cell(row, urlIdx);
          const durationStr = cell(row, durationIdx);
          const description = cell(row, descriptionIdx);

          // A row is NOT skipped for having fewer cells than the mapped columns: trailing
          // empty cells are routinely omitted by exporters, and skipping on length silently
          // dropped otherwise-valid modules. Missing cells simply read as "".
          if (!title) {
            skipped.push({ row: i + 1, reason: `No title in column ${titleIdx + 1}` });
            continue;
          }

          // Spreadsheet total/summary rows: either the cell is just the label, or it starts
          // with one AND carries no video link (a totals row never links to a video). The old
          // check was `title.includes("total")`, which silently dropped real lessons — e.g.
          // "Total Quality Management" — anywhere in the file.
          const isTotalLabel = /^(grand\s+)?(totals?|subtotals?|sum)\s*[:–—-]?$/i.test(title);
          const isTotalRow = /^(grand\s+)?(totals?|subtotals?|sum)\b/i.test(title) && !url;
          if (isTotalLabel || isTotalRow) {
            skipped.push({ row: i + 1, reason: `Looks like a summary row ("${title}")` });
            continue;
          }

          const durationSeconds = parseDurationToSeconds(durationStr);

          // Pre-flight the server's own row rules so rejections are visible BEFORE upload
          // rather than showing up as a silently smaller module list afterwards.
          let issue: string | undefined;
          let note: string | undefined;
          if (!url) issue = "No video link — the server rejects rows without one";
          else if (durationSeconds <= 0)
            // No longer a blocking problem: the server reads the real length from YouTube on
            // import. It only fails the row if that lookup also comes back empty, so this stays a
            // note and the row keeps its default selection.
            note = "No duration in the sheet — the length will be read from YouTube";

          modules.push({
            title,
            description,
            video_url: url,
            duration_seconds: durationSeconds,
            issue,
            note,
          });
        }

        setSkippedRows(skipped);

        if (modules.length === 0) {
          setErrorMsg(
            skipped.length > 0
              ? `No importable lessons found. ${skipped.length} row(s) were skipped — see the list below.`
              : "Could not parse any lessons from the CSV file. Please verify columns.",
          );
        } else {
          setParsedModules(modules);
        }
      } catch (e: any) {
        setErrorMsg(`Failed parsing CSV: ${e.message}`);
      }
    };
    reader.readAsText(selectedFile);
  };

  const importCSVMutation = useMutation({
    mutationFn: async () => {
      const selectedItems = parsedModules.filter((_, idx) => selectedIndices.includes(idx));
      if (selectedItems.length === 0) return null;

      setServerErrors([]);
      return await importModulesFromCsv(courseId, nextSequence, selectedItems);
    },
    // Report what the server actually imported, not what was selected. The endpoint answers
    // 200 with per-row errors, so a partial import used to be announced as a full one.
    onSuccess: (result) => {
      if (!result) return;
      qc.invalidateQueries({ queryKey: ["admin-course-modules", courseId] });

      if (result.failureCount > 0) {
        setServerErrors(result.errors);
        toast.warning(
          `Imported ${result.successCount} of ${result.successCount + result.failureCount} lessons — ${result.failureCount} row(s) were rejected.`,
        );
        return; // Keep the dialog open so the rejected rows and reasons stay readable.
      }

      toast.success(`Successfully imported ${result.successCount} lessons from CSV`);
      onClose();
    },
    onError: (e: Error) => {
      toast.error(`Import failed: ${e.message}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import modules from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file containing your course content. The system will look for column
            headers like Title/Focus, Description, Link/URL, and Duration to load the modules.
            Description is optional — modules without one reuse the title.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex flex-col items-center justify-center border-2 border-dashed border-muted-foreground/20 rounded-lg p-6 bg-muted/10 hover:bg-muted/20 transition-colors">
            <Upload className="h-8 w-8 text-muted-foreground mb-2" />
            <span className="text-sm font-medium text-foreground mb-1">
              {file ? file.name : "Select a CSV file to parse"}
            </span>
            <span className="text-xs text-muted-foreground mb-4">
              {file ? `${(file.size / 1024).toFixed(1)} KB` : "Supports standard .csv format"}
            </span>
            <Input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="max-w-[250px] text-xs h-9 cursor-pointer"
            />
            <Button
              variant="outline"
              size="sm"
              className="mt-4 text-xs font-semibold"
              onClick={handleDownloadTemplate}
            >
              <Download className="h-3 w-3 mr-2" />
              Download CSV Template
            </Button>
          </div>

          {errorMsg && (
            <div className="p-3 bg-destructive/10 text-destructive text-xs rounded border border-destructive/20">
              {errorMsg}
            </div>
          )}

          {skippedRows.length > 0 && (
            <div className="p-3 bg-amber-500/10 text-xs rounded border border-amber-500/30 space-y-1">
              <p className="font-semibold text-foreground">
                {skippedRows.length} row{skippedRows.length === 1 ? "" : "s"} skipped while reading
                the file
              </p>
              <ul className="text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto">
                {skippedRows.map((s) => (
                  <li key={s.row}>
                    Line {s.row}: {s.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {serverErrors.length > 0 && (
            <div className="p-3 bg-destructive/10 text-xs rounded border border-destructive/20 space-y-1">
              <p className="font-semibold text-destructive">
                {serverErrors.length} row{serverErrors.length === 1 ? "" : "s"} rejected by the
                server — fix these in the CSV and re-import just those rows
              </p>
              <ul className="text-muted-foreground space-y-0.5 max-h-28 overflow-y-auto">
                {serverErrors.map((e, i) => (
                  <li key={`${e.row}-${i}`}>
                    Row {e.row}
                    {e.title ? ` (${e.title})` : ""}: {e.errorMessage}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {parsedModules.length > 0 && columnMapping && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground p-3 bg-muted/40 rounded-lg border">
                <span className="font-semibold text-foreground">Auto-detected Columns:</span>
                <span>Title ➔ col {columnMapping.titleIdx + 1}</span>
                <span>
                  Description ➔{" "}
                  {columnMapping.descriptionIdx >= 0
                    ? `col ${columnMapping.descriptionIdx + 1}`
                    : "not found (title will be reused)"}
                </span>
                <span>Video Link ➔ col {columnMapping.urlIdx + 1}</span>
                <span>Duration ➔ col {columnMapping.durationIdx + 1}</span>
              </div>

              <div className="font-semibold text-sm">
                Parsed Lessons Preview ({parsedModules.length} found, {selectedIndices.length}{" "}
                selected)
                {parsedModules.some((m) => m.issue) && (
                  <span className="ml-2 font-normal text-xs text-amber-600 dark:text-amber-500">
                    {parsedModules.filter((m) => m.issue).length} row(s) need attention and are
                    unselected
                  </span>
                )}
              </div>
              <div className="border rounded-lg overflow-hidden max-h-[220px] overflow-y-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead className="bg-muted text-muted-foreground font-semibold uppercase text-[10px] tracking-wider border-b">
                    <tr>
                      <th className="p-2 border-r w-10 text-center">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 cursor-pointer accent-primary rounded"
                          checked={
                            selectedIndices.length === parsedModules.length &&
                            parsedModules.length > 0
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedIndices(parsedModules.map((_, i) => i));
                            } else {
                              setSelectedIndices([]);
                            }
                          }}
                        />
                      </th>
                      <th className="p-2 border-r w-10 text-center">#</th>
                      <th className="p-2 border-r">Lesson Title</th>
                      <th className="p-2 border-r">Description</th>
                      <th className="p-2 border-r">Video URL</th>
                      <th className="p-2 w-24">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y bg-background">
                    {parsedModules.map((m, idx) => (
                      <tr
                        key={idx}
                        className={`hover:bg-muted/20 ${selectedIndices.includes(idx) ? "" : "opacity-40"}`}
                      >
                        <td className="p-2 border-r text-center">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 cursor-pointer accent-primary rounded"
                            checked={selectedIndices.includes(idx)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIndices((prev) => [...prev, idx]);
                              } else {
                                setSelectedIndices((prev) => prev.filter((i) => i !== idx));
                              }
                            }}
                          />
                        </td>
                        <td className="p-2 border-r text-center font-medium text-muted-foreground">
                          {idx + 1}
                        </td>
                        <td className="p-2 border-r font-medium text-foreground max-w-[200px]">
                          <span className="block truncate">{m.title}</span>
                          {m.issue && (
                            <span className="block text-[10px] font-normal text-amber-600 dark:text-amber-500">
                              {m.issue}
                            </span>
                          )}
                          {m.note && (
                            <span className="block text-[10px] font-normal text-muted-foreground">
                              {m.note}
                            </span>
                          )}
                        </td>
                        <td
                          className="p-2 border-r text-muted-foreground truncate max-w-[200px]"
                          title={m.description}
                        >
                          {m.description || <span className="italic">Reuses title</span>}
                        </td>
                        <td
                          className="p-2 border-r text-blue-500 font-mono truncate max-w-[200px]"
                          title={m.video_url}
                        >
                          {m.video_url || (
                            <span className="text-muted-foreground italic">None</span>
                          )}
                        </td>
                        <td className="p-2 font-mono">
                          {formatSecondsToReadable(m.duration_seconds)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => importCSVMutation.mutate()}
            disabled={selectedIndices.length === 0 || importCSVMutation.isPending}
            className="min-w-[120px]"
          >
            {importCSVMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              `Import ${selectedIndices.length} Modules`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
